/**
 * useTabVisibility.js
 * 
 * React hook for detecting when the browser tab is visible or hidden.
 * Useful for pausing/resuming work to save battery on hidden tabs.
 * 
 * Uses the Page Visibility API:
 * - document.hidden: true when tab is hidden
 * - document.visibilityState: 'visible' | 'hidden' | 'prerender'
 * - 'visibilitychange' event: fires when visibility changes
 * 
 * Use cases:
 * - Pause video/audio when tab is hidden
 * - Stop background intervals/animations
 * - Pause network polling
 * - Defer analytics/telemetry
 * 
 * @example
 * import { useTabVisibility } from '../hooks/useTabVisibility';
 * 
 * function VideoPlayer() {
 *   const isVisible = useTabVisibility();
 * 
 *   useEffect(() => {
 *     if (!isVisible) {
 *       videoRef.current?.pause();
 *     }
 *   }, [isVisible]);
 * }
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Get current visibility state
 * @returns {boolean} - true if tab is visible, false if hidden
 */
const getVisibilityState = () => {
  if (typeof document === 'undefined') return true;
  return !document.hidden;
};

/**
 * Hook that returns true when tab is visible, false when hidden
 * 
 * @returns {boolean} - Current visibility state
 */
export const useTabVisibility = () => {
  const [isVisible, setIsVisible] = useState(getVisibilityState);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(getVisibilityState());
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

/**
 * Hook that executes callbacks when visibility changes
 * 
 * @param {Object} callbacks - Object with onVisible and onHidden callbacks
 * @param {Function} callbacks.onVisible - Called when tab becomes visible
 * @param {Function} callbacks.onHidden - Called when tab becomes hidden
 * 
 * @example
 * useVisibilityChange({
 *   onVisible: () => console.log('Tab is now visible'),
 *   onHidden: () => console.log('Tab is now hidden')
 * });
 */
export const useVisibilityChange = ({ onVisible, onHidden }) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = getVisibilityState();
      
      if (isVisible && onVisible) {
        onVisible();
      } else if (!isVisible && onHidden) {
        onHidden();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onVisible, onHidden]);
};

/**
 * Hook that pauses/resumes an interval based on tab visibility
 * 
 * @param {Function} callback - Function to run on each interval
 * @param {number} delay - Delay in milliseconds (null to disable)
 * @param {boolean} pauseWhenHidden - Whether to pause when tab is hidden (default: true)
 * 
 * @example
 * useVisibilityAwareInterval(() => {
 *   fetchUpdates();
 * }, 5000, true);
 */
export const useVisibilityAwareInterval = (callback, delay, pauseWhenHidden = true) => {
  const isVisible = useTabVisibility();

  useEffect(() => {
    if (!delay) return;
    
    // If pauseWhenHidden is true and tab is hidden, don't start interval
    if (pauseWhenHidden && !isVisible) return;

    const id = setInterval(callback, delay);

    return () => clearInterval(id);
  }, [callback, delay, pauseWhenHidden, isVisible]);
};

/**
 * Hook that returns visibility state with additional metadata
 * 
 * @returns {Object} - Visibility information object
 * 
 * @example
 * const { isVisible, visibilityState, hiddenTime } = useVisibilityInfo();
 */
export const useVisibilityInfo = () => {
  const [info, setInfo] = useState(() => ({
    isVisible: getVisibilityState(),
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'visible',
    hiddenTime: null,
    visibleTime: Date.now()
  }));

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = getVisibilityState();
      const visibilityState = document.visibilityState;
      const now = Date.now();

      setInfo(prev => ({
        isVisible,
        visibilityState,
        hiddenTime: isVisible ? prev.hiddenTime : now,
        visibleTime: isVisible ? now : prev.visibleTime
      }));
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return info;
};

/**
 * Standalone function to pause work when tab is hidden
 * Useful for class components or non-React code
 * 
 * @param {Object} config - Configuration object
 * @param {Function} config.onVisible - Called when tab becomes visible
 * @param {Function} config.onHidden - Called when tab becomes hidden
 * @returns {Function} - Cleanup function to remove listener
 * 
 * @example
 * const cleanup = pauseOnHidden({
 *   onVisible: () => player.play(),
 *   onHidden: () => player.pause()
 * });
 * 
 * // Later...
 * cleanup();
 */
export const pauseOnHidden = ({ onVisible, onHidden }) => {
  const handleVisibilityChange = () => {
    const isVisible = getVisibilityState();
    
    if (isVisible && onVisible) {
      onVisible();
    } else if (!isVisible && onHidden) {
      onHidden();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

export default {
  useTabVisibility,
  useVisibilityChange,
  useVisibilityAwareInterval,
  useVisibilityInfo,
  pauseOnHidden,
  getVisibilityState
};
